import DepartmentSelect from '@/components/Form/Shared/DepartmentSelect'
import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import LinkIcons from '@/icons/LinkIcons'
import {
    CreateShareLink,
    EditShareRecipient,
    GetPublicShareLinks,
    RevokeShareLink,
} from '@/modules/Drive/services'
import {
    EditShareRecipientParams,
    ObjectType,
    RecipientResponse,
    SharedLinkResponse,
    ShareLinkParams,
    ShareRecipientResponse,
} from '@/modules/Drive/types'
import { PermissionLevel } from '@/modules/Drive/utils/DrivePermission/permission'
import useAuthStore from '@/stores/useAuthStore'
import { Sections } from '@/stores/useDriveStore'
import datacentral from '@/utils/axios/datacentral'
import { copyToClipboard } from '@/utils/function'
import { useQueryClient } from '@tanstack/react-query'
import { t } from 'i18next'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { Button, FormItem, Tabs } from 'vnetwork-ui'
import ShareRecipients from './ShareRecipients'
import PublicLinksSection from './PublicLinksSection'
import shareObjectValidate from './yup'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface ShareObjectFormProps {
    objectName: string
    objectKey: string
    objectType: ObjectType
    onClose: () => void
}

interface FormFields {
    share_is_public: boolean
    employee_id: number[]
    department_id: number[]
    permission_level: PermissionLevel | ''
}

const ShareObjectForm = ({
    onClose,
    objectName,
    objectKey,
    objectType,
}: ShareObjectFormProps) => {
    const queryClient = useQueryClient()
    const employee_id = useAuthStore((state) => state.employee?.employee_id)
    const [token, setToken] = useState('')
    const [receipients, setReceipients] = useState<RecipientResponse[]>([])
    // Add dirty tracking state
    const [dirtyRecipients, setDirtyRecipients] = useState<
        Map<number, PermissionLevel>
    >(new Map())
    const { handleSubmit, control, watch, setError, clearErrors } =
        useForm<FormFields>({
            defaultValues: {
                share_is_public: false,
                employee_id: [],
                department_id: [],
                permission_level: PermissionLevel.READ,
            },
        })
    const isPublic = watch('share_is_public')
    // watch employee_id and department_id
    const employee_id_watch = watch('employee_id')
    const department_id_watch = watch('department_id')
    const isEditPermission =
        employee_id_watch.length === 0 && department_id_watch.length === 0

    const [activeTab, setActiveTab] = useState<
        'employee' | 'department' | 'public'
    >('employee')

    // Mutation: Create Share Link
    const {
        mutateAsync: createShareLink,
        isPending: isCreateShareLinkPending,
    } = useMutationWithHandler(
        { mutationFn: CreateShareLink },
        {
            reactQueryKeys: QUERY_KEYS.DRIVE,
            successMessage: t('share-created'),
            onSuccess: (data: any) => {
                const url = `${window.location.origin}/drive/${Sections.Shared}?token=${data.result}`
                setToken(url)
            },
            onError: (error: any) => {
                toast.error(error.message)
            },
        }
    )

    // Mutation: Edit Share Recipient
    const {
        mutateAsync: editShareRecipient,
        isPending: isEditShareRecipientPending,
    } = useMutationWithHandler(
        { mutationFn: EditShareRecipient },
        {
            reactQueryKeys: QUERY_KEYS.DRIVE,
            successMessage: t('share-updated'),
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: [
                        QUERY_KEYS.DRIVE_SHARE_RECIPIENTS,
                        objectType,
                        objectKey,
                    ],
                })
                onClose()
            },
            onError: (error: any) => {
                toast.error(error.message)
            },
        }
    )

    const isPending = isEditShareRecipientPending || isCreateShareLinkPending

    const { data } = useQueryWithHandler<ShareRecipientResponse>({
        queryKey: [QUERY_KEYS.SHARE_RECIPIENTS, objectType, objectKey],
        queryFn: async () => {
            const { data } = await datacentral.post(
                '/drive/share/recipient-list',
                {
                    share_object_type: objectType,
                    share_object_key: objectKey,
                }
            )
            return data.results
        },
        enabled: !!objectType && !!objectKey,
        staleTime: 0,
    })

    useEffect(() => {
        if (data) {
            setReceipients(data.recipient_list)
        }
    }, [data])

    // Query: Fetch Public Share Links
    const { data: publicLinksData } = useQueryWithHandler<SharedLinkResponse[]>(
        {
            queryKey: [QUERY_KEYS.DRIVE_PUBLIC_LINKS, objectType, objectKey],
            queryFn: async () => {
                const response = await GetPublicShareLinks(
                    objectType,
                    objectKey
                )
                return response.results
            },
            enabled: !!objectType && !!objectKey,
            staleTime: 0,
        }
    )

    const publicLinks = publicLinksData || []

    const { mutateAsync: revokePublicLink } = useMutationWithHandler(
        { mutationFn: RevokeShareLink },
        {
            reactQueryKeys: QUERY_KEYS.DRIVE_PUBLIC_LINKS,
            successMessage: t('drive-link-revoked'),
            errorMessage: t('drive-link-revoked-failed'),
        }
    )

    const handleDirtyChange = (
        isDirty: boolean,
        recipient_id: number,
        permission_level: PermissionLevel
    ) => {
        setDirtyRecipients((prev) => {
            const newMap = new Map(prev)
            if (isDirty) newMap.set(recipient_id, permission_level)
            else newMap.delete(recipient_id)
            return newMap
        })
    }

    const isDirty = dirtyRecipients.size > 0

    const onSubmit: SubmitHandler<FormFields> = async (data) => {
        // Skip validation if editing existing recipients
        if (isDirty) {
            const editShareRecipientParams: EditShareRecipientParams[] =
                Array.from(dirtyRecipients.entries())
                    .map(([recipient_id, permission_level]) => {
                        const recipient = receipients?.find(
                            (r) => r.recipient_id === Number(recipient_id)
                        )
                        if (!recipient?.share_recipient_id) {
                            throw new Error(
                                `Share recipient ID not found for recipient ${recipient_id}`
                            )
                        }
                        return {
                            share_recipient_id: recipient.share_recipient_id,
                            share_object_key: objectKey,
                            share_object_type: objectType,
                            share_permission_level: permission_level,
                            recipient_id: Number(recipient_id),
                            revoke: permission_level === null,
                            share_link_created_by: employee_id,
                            share_recipient_type:
                                recipient.share_recipient_type as
                                    | 'employee'
                                    | 'department',
                        }
                    })
                    .filter((param) => param !== null)
            await editShareRecipient(editShareRecipientParams)
            setDirtyRecipients(new Map())
        } else if (employee_id) {
            // Apply validation for creating new share link
            try {
                if (!isPublic) {
                    await shareObjectValidate.validate(data, {
                        abortEarly: false,
                    })
                }
                // Clear any existing errors if validation passes
                clearErrors()
            } catch (error: any) {
                if (error.inner && error.inner.length > 0) {
                    // Set field-specific errors
                    error.inner.forEach((err: any) => {
                        setError(err.path as keyof FormFields, {
                            type: 'manual',
                            message: err.message,
                        })
                    })
                } else if (error.errors && error.errors.length > 0) {
                    // Fallback: Show all validation errors in toast
                    const errorMessages = error.errors.join(', ')
                    toast.error(`Validation errors: ${errorMessages}`)
                } else {
                    toast.error(t('validation-error'))
                }
                return
            }

            const shareLinkParams: ShareLinkParams = {
                share_object_key: objectKey,
                share_object_type: objectType,
                share_is_public: data.share_is_public,
                share_permission_level:
                    data.permission_level as PermissionLevel,
                share_link_created_by: employee_id,
                share_with_employees: isPublic ? [] : data.employee_id,
                share_with_department: isPublic ? [] : data.department_id,
            }
            await createShareLink(shareLinkParams)
        }
    }

    if (token) {
        return (
            <div>
                <p>
                    {t('shared-object')}:{' '}
                    <span className="text-blue-600">{objectName}</span>
                </p>
                <div className="flex flex-col gap-y-2">
                    <p className="text-sm text-gray-500">{t('share-link')}:</p>
                    <div className="flex flex-col gap-y-2 items-end">
                        <div className="w-full bg-muted bg-gray-100 p-3 rounded-l-md overflow-x-auto whitespace-nowrap text-sm">
                            {token}
                        </div>
                        <div className="flex gap-x-2">
                            <Button
                                onClick={() => setToken('')}
                                variant="text"
                                className="w-fit transition-all duration-200"
                                size="small"
                            >
                                {t('link-settings')}
                            </Button>
                            <Button
                                onClick={() => copyToClipboard(token, t)}
                                className="w-fit py-3 transition-all duration-200"
                                size="small"
                                startIcon={<LinkIcons className="h-4 w-4" />}
                            >
                                {t('copy-link')}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div>
            <p>
                {t('shared-object')}:{' '}
                <span className="text-blue-600">{objectName}</span>
            </p>
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col gap-y-8 pt-5 "
            >
                {!isPublic && !isDirty && (
                    <>
                        <EmployeeSelect
                            control={control}
                            name="employee_id"
                            multiple
                            label={t('select-employee-to-share')}
                            excludeEmployeeIds={
                                employee_id ? [employee_id] : []
                            }
                        />
                        <DepartmentSelect
                            control={control}
                            name="department_id"
                            multiple
                            label={t('select-department-to-share')}
                        />
                    </>
                )}
                {!isDirty && (
                    <FormItem
                        control={control}
                        name="permission_level"
                        type="select"
                        label={t('select-permission')}
                        placeholder={t('select-permission')}
                        options={[
                            { label: t('can-edit'), value: 'edit' },
                            { label: t('can-view'), value: 'read' },
                            {
                                label: t('can-view-and-not-download'),
                                value: 'view',
                            },
                        ]}
                        direction="row"
                    />
                )}
                {isEditPermission && !isPublic && (
                    <div className="flex flex-col ">
                        <p>{t('existing-access')}</p>
                        <Tabs
                            tabs={[
                                {
                                    label: t('shared-with-employees'),
                                    value: 'employee',
                                },
                                {
                                    label: t('shared-with-departments'),
                                    value: 'department',
                                },
                                {
                                    label: t('drive-public-share-links'),
                                    value: 'public',
                                },
                            ]}
                            activeTab={activeTab}
                            onChangeTab={(value) =>
                                setActiveTab(
                                    value as
                                        | 'employee'
                                        | 'department'
                                        | 'public'
                                )
                            }
                            size="small"
                            tabClassName="flex-1"
                        />
                        <div className="flex flex-col">
                            {activeTab === 'public' ? (
                                <PublicLinksSection
                                    publicLinks={publicLinks}
                                    onRevokeLink={revokePublicLink}
                                    isLoading={isEditShareRecipientPending}
                                />
                            ) : (
                                <ShareRecipients
                                    recipients={receipients}
                                    onDirtyChange={handleDirtyChange}
                                    isDirty={isDirty}
                                    activeTab={activeTab}
                                    owner={data?.owner}
                                />
                            )}
                        </div>
                        {isDirty && (
                            <div className="text-yellow-600 text-xs mt-5">
                                {t('unsaved-changes')}
                            </div>
                        )}
                    </div>
                )}
                {!isDirty && (
                    <FormItem
                        control={control}
                        name="share_is_public"
                        type="checkbox"
                        placeholder={t('create-public-share-link')}
                    />
                )}
                <div className="flex items-center justify-end gap-x-2">
                    <Button
                        disabled={isPending}
                        onClick={() => {
                            onClose()
                        }}
                        variant="outlined"
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        disabled={isPending}
                        loading={isPending}
                        type="submit"
                    >
                        {isDirty ? t('save') : t('create-folder-confirm')}
                    </Button>
                </div>
            </form>
        </div>
    )
}

export default ShareObjectForm
